import { api, el, fmt, itemIcon } from './dom.js';

const MODE_LABELS = {
  one: 'Sorteia 1 destes',
  several: 'Sorteia alguns destes',
  all: 'Vem tudo isto'
};

const probabilityFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

function cronsText(crons) {
  if (!crons || !(crons.max > 0)) return '';
  if (crons.min === crons.max) return `${fmt.int(crons.max)} Crons`;
  return `${fmt.int(crons.min)} a ${fmt.int(crons.max)} Crons${crons.estimate ? ' (estimativa)' : ''}`;
}

// Média esperada, quando a caixa tem probabilidades oficiais; senão a faixa.
function valueText(node) {
  if (node.expected && node.expected.value > 0 && node.crons?.min !== node.crons?.max) return `média ${fmt.int(node.expected.value)} Crons${node.expected.partial ? '*' : ''}`;
  return cronsText(node.crons);
}

// Nome já termina em "x50"? Então a quantidade 1 não se repete.
function label(node) {
  return node.quantity > 1 ? `${node.name} ×${fmt.int(node.quantity)}` : node.name;
}

function rewardRow(node, onAdd, depth) {
  const value = valueText(node);
  const head = el('div', { className: 'mc-reward-row' }, [
    itemIcon(node.iconPath, { size: 'sm' }),
    el('span', { className: 'mc-reward-name' }, [
      el('span', { text: label(node) }),
      node.probability !== null && node.probability !== undefined && !node.guaranteed
        ? el('small', { className: 'mc-reward-chance', title: 'Probabilidade oficial (Guia do Aventureiro)', text: `${probabilityFormat.format(node.probability * 100)}%` })
        : null,
      node.guaranteed ? el('small', { className: 'mc-reward-tag', text: 'garantido' }) : null,
      node.rewardItem ? el('small', { className: 'mc-reward-tag', text: node.rewardItem.name }) : null,
      value ? el('small', { className: 'mc-reward-crons', text: value }) : null
    ]),
    el('button', { type: 'button', className: 'mc-link-button', text: '+ Recebi', title: `Adicionar ${node.name} ao que veio no pacote`, onclick: (event) => { event.preventDefault(); onAdd(node); } })
  ]);
  if (!node.outcomes?.length) {
    return el('li', {}, [head, node.isContainer && depth > 0 ? el('small', { className: 'mc-muted', text: 'Conteúdo ainda não carregado.' }) : null]);
  }
  const details = el('details', { className: 'mc-reward-group' }, [
    el('summary', {}, [head]),
    el('p', { className: 'mc-reward-mode' }, [
      MODE_LABELS[node.mode] || '',
      node.probabilitySource ? ' · ' : null,
      node.probabilitySource ? el('a', { href: node.probabilitySource.sourceUrl, target: '_blank', rel: 'noopener', text: 'probabilidades oficiais' }) : null
    ]),
    el('ul', { className: 'mc-reward-list' }, node.outcomes.map((child) => rewardRow(child, onAdd, depth + 1)))
  ]);
  return el('li', {}, [details]);
}

// Cabeçalho: média esperada do pacote (probabilidades oficiais) e quanto isso
// rende por Pérola no preço de referência; sem probabilidades, só a faixa.
function summary(items, referencePearls) {
  const expected = items.every((item) => item.expected) ? items.reduce((total, item) => total + item.expected.value, 0) : null;
  const partial = items.some((item) => item.expected?.partial);
  const range = items.reduce((total, item) => (total && item.crons ? { min: total.min + item.crons.min, max: total.max + item.crons.max, estimate: total.estimate || item.crons.estimate } : null), { min: 0, max: 0, estimate: false });
  const lines = [];
  if (expected > 0) {
    lines.push(el('p', { className: 'mc-rewards-expected' }, [
      'Média esperada: ',
      el('strong', { text: `${fmt.int(expected)} Crons` }),
      referencePearls ? ` · ${fmt.cpp(expected / referencePearls)} Cron/Pérola no preço de referência (${fmt.int(referencePearls)} Pérolas)` : ''
    ]));
    lines.push(el('small', { text: `Calculada com as probabilidades oficiais do Guia do Aventureiro${partial ? '. *Alguns itens sem probabilidade oficial contaram como 0' : ''}. Cada abertura é um sorteio: o que você recebe pode ficar bem acima ou abaixo.` }));
  } else {
    lines.push(el('small', { text: `${range && range.max > 0 ? `${cronsText(range)} possíveis. ` : ''}Sem probabilidades oficiais para este pacote.` }));
  }
  return lines;
}

// "Pode vir no pacote": árvore de recompensas (baús dentro de baús), com a
// probabilidade oficial de cada resultado, a média esperada e um botão para
// registrar o que o jogador realmente recebeu.
export async function renderPackageRewards(host, packageId, { onAdd, referencePearls = null }) {
  host.replaceChildren(el('p', { className: 'mc-muted', text: 'Buscando o que pode vir no pacote…' }));
  let data;
  try {
    data = await api(`/api/tools/cron-calculator/packages/${encodeURIComponent(packageId)}/rewards`);
  } catch {
    host.replaceChildren();
    return;
  }
  if (!data.items.some((item) => item.isContainer)) { host.replaceChildren(); return; }
  host.replaceChildren(...[
    el('div', { className: 'mc-rewards-head' }, [el('strong', { text: 'Pode vir no pacote' }), ...summary(data.items, referencePearls)]),
    el('p', { className: 'mc-muted', text: 'Abriu o pacote? Toque em “+ Recebi” no que veio para adicionar à conta.' }),
    el('ul', { className: 'mc-reward-list' }, data.items.map((item) => rewardRow(item, onAdd, 0))),
    data.complete ? null : el('button', {
      type: 'button',
      className: 'mc-secondary',
      text: 'Carregar mais recompensas',
      onclick: () => renderPackageRewards(host, packageId, { onAdd, referencePearls })
    })
  ].filter(Boolean));
}
